import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Card, Row, Col, Button, Typography, Space, Spin, Progress, Drawer, InputNumber, Tag, Modal, Radio, DatePicker, Tooltip, Badge, Tabs, Select, message, Form } from 'antd';
import { UserAddOutlined, ReadOutlined, CopyOutlined, SearchOutlined, BookOutlined, RobotOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useAuth } from '../contexts/AuthContext';
import useCoachCourses from '../hooks/useCoachCourses';
import CourseSelector from '../components/CourseSelector';
import MyCourseSelector from '../components/MyCourseSelector'; // 新增导入
import MemberSelector from '../components/MemberSelector';
import PostClassFeedback from '../components/PostClassFeedback';

const formatDate = 'YYYY-MM-DD';
const formatTime = 'HH:mm';

const { Text } = Typography;

const DEFAULT_CONSUME = 1;
const SLOT_DURATION_MIN = 30;
const DAY_START = '09:00';
const DAY_END = '21:00';

const getWeekStart = (date) => {
  const target = dayjs.isDayjs(date) ? date : dayjs(date);
  const normalized = target.startOf('day');
  return normalized.day() === 0
    ? normalized.subtract(6, 'day')
    : normalized.startOf('week').add(1, 'day');
};

// const { RangePicker } = DatePicker;

// Helper to build payload for a single member + single course at a specific datetime
const buildMemberPayload = ({ member, course, dateTime, period, creator, coach, id, activeTab }) => {
  const combined = dateTime.second(0);

  return {
    accClasses: 0,
    babyId: member.id,
    babyName: member.babyName,
    briefSummary: '',
    coach: coach,
    completion: '',
    consumeClass: activeTab === 'home' ? 0 : (period || DEFAULT_CONSUME),
    createUser: creator,
    customerName: member.customerName || '',
    details: course
      ? [{
          sportsItem: course.eventTitle,
          sportsContent: course.courseContext || '',
        }]
      : [],
    heartRate: '',
    id: id && typeof id === 'number' ? id : null,
    points: '',
    restTimes: '',
    suggestion: '',
    trainDate: combined.format('YYYY-MM-DD HH:mm'),
    trainTimes: activeTab === 'home' ? 0 : (period || DEFAULT_CONSUME),
    updateUser: creator,
    teachingMode: member.deliveryType || '一对一',
  };
};

// 根据deliveryType值返回对应的显示文本
const getDisplayDeliveryType = (deliveryType) => {
  switch (deliveryType) {
    case '一对一':
    case 'SVIP专属':
      return 'SVIP专属';
    case '一对二':
    case 'VIP私教':
      return 'VIP私教';
    case '一对多':
    case '私教小团':
      return '私教小团';
    default:
      return deliveryType || '私教小团';
  }
};

// Generate 30-minute slots for a given date
const generateTimeSlots = (date) => {
  const [startHour, startMinute] = DAY_START.split(':').map(Number);
  const [endHour, endMinute] = DAY_END.split(':').map(Number);

  const start = dayjs(date).hour(startHour).minute(startMinute).second(0);
  const end = dayjs(date).hour(endHour).minute(endMinute).second(0);

  const slots = [];
  let current = start;
  while (current.isBefore(end) || current.isSame(end)) {
    slots.push({
      slotId: current.format(formatTime),
      startTime: current,
      label: current.format(formatTime),
    });
    current = current.add(SLOT_DURATION_MIN, 'minute');
  }
  return slots;
};

// Build week days from weekStart
const buildWeekDays = (weekStart) => {
  const weekDays = [];
  const weekLabels = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
  for (let i = 0; i < 7; i += 1) {
    const d = weekStart.add(i, 'day');
    weekDays.push({
      date: d,
      label: weekLabels[i] || d.format('ddd'),
    });
  }
  return weekDays;
};

// Map backend pageList data to local schedule rows structure
const mapPageListToRows = (list) => {
  const allRows = {};
  if (!Array.isArray(list)) return allRows;

  list.forEach((item) => {
    if (!item.trainDate) return;
    const dt = dayjs(item.trainDate);
    if (!dt.isValid()) return;
    const dateKey = dt.format(formatDate);

    if (!allRows[dateKey]) allRows[dateKey] = {};

    const rows = allRows[dateKey];
    const slotId = dt.format(formatTime);
    if (!rows[slotId]) {
      rows[slotId] = {
        slotId,
        startTime: dt,
        period: Number(item.trainTimes) || DEFAULT_CONSUME,
        entries: [],
      };
    }

    const row = rows[slotId];

    // Find or create a single entry per row for simplicity (all members同一组课)
    let entry = row.entries.find(e => e.id === item.id);
    if (!entry) {
      entry = {
        id: item.id,
        members: [],
        courses: [],
        isExisting: true,
        updateFlag: 0,
        completeClass: item.completeClass || '',
        teachingMode: item.teachingMode || '',
        originalMemberIds: [], // 存储原始会员ID列表
      };
      row.entries.push(entry);
    } else {
      // 如果entry已存在，也更新completeClass字段
      entry.completeClass = item.completeClass || '';
      entry.teachingMode = item.teachingMode || '';
    }

    // Member
    const member = {
      id: item.babyId,
      babyName: item.babyName,
      customerName: item.customerName,
      customerId: item.customerId,
      classId: item.classId,
      deliveryType: item.teachingMode,
    };
    if (!entry.members.find((m) => m.id === member.id)) {
      entry.members.push(member);
      // 记录原始会员ID（用于判断是否是新添加的会员）
      if (!entry.originalMemberIds.includes(member.id)) {
        entry.originalMemberIds.push(member.id);
      }
    }

    // Courses from details
    if (Array.isArray(item.details)) {
      item.details.forEach((d) => {
        const courseKey = `${d.sportsItem || ''}-${d.sportsContent || ''}`;
        if (!entry.courses.find((c) => c._key === courseKey)) {
          entry.courses.push({
            _key: courseKey,
            courseKbn: d.sportsItem,
            eventTitle: d.sportsItem,
            courseContext: d.sportsContent,
            seq: d.sportsItem, // 使用eventTitle作为seq，用于CourseSelector识别
          });
        }
      });
    }
  });

  return allRows;
};

const ScheduCourse = ({ user }) => {
  const { apiClient } = useAuth();

  // 设置 dayjs 为中文
  dayjs.locale('zh-cn');

  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const coach = storedUser.coach || '';

  const [currentDate, setCurrentDate] = useState(() => dayjs().startOf('day'));
  const [weekStart, setWeekStart] = useState(() => getWeekStart(dayjs()));
  const [pageLoading, setPageLoading] = useState(false);

  const [scheduleRows, setScheduleRows] = useState({}); // dateString -> { slotId -> row }

  // Drawer state for selectors
  const [activeRowSlotId, setActiveRowSlotId] = useState(null);
  const [activeSelectorType, setActiveSelectorType] = useState(null); // 'member' | 'course' | null

  // New state for my course selector modal
  const [myCourseSelectorModalVisible, setMyCourseSelectorModalVisible] = useState(false);

  // Saving state and progress
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ completed: 0, total: 0, currentMemberName: '' });

  // Copy modal state
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copyTargetDate, setCopyTargetDate] = useState(null);
  const [copyMode, setCopyMode] = useState('overwrite');

  // Copy course confirmation modal
  const [copyCourseModal, setCopyCourseModal] = useState(false);
  const [copySlotId, setCopySlotId] = useState(null);

  // Delete entry modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteSlotId, setDeleteSlotId] = useState(null);
  const [deleteEntryId, setDeleteEntryId] = useState(null);

    // Remove member modal state
    const [removeMemberModalVisible, setRemoveMemberModalVisible] = useState(false);
    const [removeMemberSlotId, setRemoveMemberSlotId] = useState(null);
    const [removeMemberId, setRemoveMemberId] = useState(null);
    const [removeMember, setRemoveMember] = useState(null);

    // Single save modal state
    const [singleSaveModalVisible, setSingleSaveModalVisible] = useState(false);
    const [singleSaveSlotId, setSingleSaveSlotId] = useState(null);
    const [singleSaveEntryId, setSingleSaveEntryId] = useState(null);

    // Active tab state
    const [activeTab, setActiveTab] = useState('offline'); // 'offline' or 'home'
    const [printing, setPrinting] = useState(false);

    // Training feedback modal state
    const [trainingFeedbackModalVisible, setTrainingFeedbackModalVisible] = useState(false);
    const [currentTrainingRecord, setCurrentTrainingRecord] = useState(null);

    // Training feedback form
    const [trainingFeedbackForm] = Form.useForm();

    // Ref for PDF export
    const trainingFeedbackScheduleRef = useRef();
    const schedulePrintRef = useRef(null);
    const scheduleScrollRef = useRef(null);

    // Future scheduling modal state
    const [futureSchedulingModalVisible, setFutureSchedulingModalVisible] = useState(false);
    const [futureSchedules, setFutureSchedules] = useState([]);
    const [disabledDates, setDisabledDates] = useState([]);
    const [selectedDates, setSelectedDates] = useState([]);
    const [datePickerOpen, setDatePickerOpen] = useState(true);
    const [schedulingEntry, setSchedulingEntry] = useState(null);
    
    // Change time modal state
    const [changeTimeModalVisible, setChangeTimeModalVisible] = useState(false);
    const [changeTimeSlotId, setChangeTimeSlotId] = useState(null);
    const [changeTimeEntryId, setChangeTimeEntryId] = useState(null);
    const [selectedNewDate, setSelectedNewDate] = useState(null);
    const [selectedNewTime, setSelectedNewTime] = useState(null);

    // AI Coach state
    const [aiCoachLoading, setAiCoachLoading] = useState({}); // Track loading state for each slot

    // 用于跟踪当前正在选择会员的特定entry（用于空白明细行选择会员）
    const [selectingMemberEntryId, setSelectingMemberEntryId] = useState(null);
    // 用于跟踪当前正在选择课程的特定entry
    const [selectingCourseEntryId, setSelectingCourseEntryId] = useState(null);

  const weekEnd = useMemo(() => weekStart.add(6, 'day'), [weekStart]);

  const weekDays = useMemo(() => buildWeekDays(weekStart), [weekStart]);

  const timeSlots = useMemo(() => generateTimeSlots(currentDate), [currentDate]);

  const currentRows = useMemo(() => scheduleRows[currentDate.format(formatDate)] || {}, [scheduleRows, currentDate]);

  const closeDrawer = () => {
    setActiveRowSlotId(null);
    setActiveSelectorType(null);
  };

  const handlePrevWeek = () => {
    const newStart = weekStart.subtract(1, 'week');
    setWeekStart(newStart);
    setCurrentDate(newStart);
  };

  const handleNextWeek = () => {
    const newStart = weekStart.add(1, 'week');
    setWeekStart(newStart);
    setCurrentDate(newStart);
  };

  const handleSelectDate = (date) => {
    if (!date) return;
    const normalized = date.startOf('day');
    setCurrentDate(normalized);
    setWeekStart(getWeekStart(normalized));
  };

    const handleTabChange = (key) => {
    setActiveTab(key);
    };

    const handlePrintSchedule = async () => {
    if (!schedulePrintRef.current) {
      message.error('无法打印排课表：页面未准备好');
      return;
    }
    setPrinting(true);
    const scrollEl = scheduleScrollRef.current;
    const originalStyles = scrollEl
      ? {
          maxHeight: scrollEl.style.maxHeight,
          overflow: scrollEl.style.overflow,
          overflowY: scrollEl.style.overflowY,
        }
      : null;

    try {
      if (scrollEl) {
        scrollEl.style.maxHeight = 'none';
        scrollEl.style.overflow = 'visible';
        scrollEl.style.overflowY = 'visible';
      }
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const canvas = await html2canvas(schedulePrintRef.current, {
        scale: 2,
        useCORS: true,
        scrollY: -window.scrollY,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const printableWidth = pageWidth - margin * 2;
      const printableHeight = pageHeight - margin * 2;
      const imgHeight = (canvas.height * printableWidth) / canvas.width;
      let heightLeft = imgHeight - printableHeight;

      pdf.addImage(imgData, 'PNG', margin, margin, printableWidth, imgHeight);

      while (heightLeft > 0) {
        const position = margin - (imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, printableWidth, imgHeight);
        heightLeft -= printableHeight;
      }

      const tabName = activeTab === 'offline' ? '线下课程' : '家庭训练';
      pdf.save(`${currentDate.format('YYYYMMDD')}-${tabName}.pdf`);
      message.success('排课表已导出');
    } catch (error) {
      console.error('打印排课表失败:', error);
      message.error('打印排课表失败，请稍后重试');
    } finally {
      if (scrollEl && originalStyles) {
        scrollEl.style.maxHeight = originalStyles.maxHeight || '';
        scrollEl.style.overflow = originalStyles.overflow || '';
        scrollEl.style.overflowY = originalStyles.overflowY || '';
      }
      setPrinting(false);
    }
    };

  const handleOpenTrainingFeedback = (entry) => {
    // 设置当前训练记录
    setCurrentTrainingRecord(entry);
    setTrainingFeedbackModalVisible(true);
  };

  const handleOpenFutureScheduling = async (entry) => {
    const babyId = entry.members[0].id;
    const today = dayjs();
    const startDate = today.format('YYYY-MM-DD');
    const endDate = today.add(1, 'month').format('YYYY-MM-DD');
    const payload = {
      babyId,
      pageSize: 99999,
      pageNo: 1,
      searchStartDate: startDate,
      searchEndDate: endDate
    };
    try {
      const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/pageList', payload);
      if (resp.data.code === 200) {
        const list = resp.data.data.list;
        const trainDates = list.map(item => dayjs(item.trainDate).format('YYYY-MM-DD'));
        setDisabledDates(trainDates);
        setFutureSchedules(list);
        
        // 从原始数据中查找与当前entry对应的完整信息，以获取原始的trainDate
        const originalEntryData = list.find(item => item.babyId === babyId && item.id === entry.id);
        
        // 创建增强的schedulingEntry，包含原始的trainDate信息
        const enhancedSchedulingEntry = {
          ...entry,
          trainDate: originalEntryData ? originalEntryData.trainDate : null
        };
        
        setSchedulingEntry(enhancedSchedulingEntry);
        setSelectedDates([]);
        setFutureSchedulingModalVisible(true);
      } else {
        message.error('获取排课计划失败');
      }
    } catch (error) {
      message.error('获取排课计划失败');
    }
  };

  const handleConfirmScheduling = () => {
    if (selectedDates.length === 0) {
      message.warning('请选择至少一个日期');
      return;
    }
    Modal.confirm({
      title: '确认排课',
      content: `确定要为 ${schedulingEntry.members[0].babyName} 排课 ${selectedDates.length} 个日期吗？`,
      onOk: async () => {
        await handleScheduleConfirm();
      },
    });
  };

  const handleScheduleConfirm = async () => {
    const member = schedulingEntry.members[0];
    const creator = user?.name || storedUser.name || '';
    const coachId = coach;
    
    // 从原始调度条目中获取第一个有效的课程详情和时间信息
    const originalDetails = schedulingEntry.courses && schedulingEntry.courses.length > 0 
      ? schedulingEntry.courses.map(course => ({
          sportsItem: course.eventTitle,
          sportsContent: course.courseContext || '',
        }))
      : [
          {
            sportsItem: "核心/俯地收腹",
            sportsContent: ""
          },
          {
            sportsItem: "有氧/小碎步行进",
            sportsContent: ""
          }
        ];
    
    // 从原始调度条目中获取时间信息
    const originalTrainTime = schedulingEntry.trainDate ? dayjs(schedulingEntry.trainDate).format('HH:mm') : '20:00';
    
    for (const date of selectedDates) {
      const payload = {
        accClasses: 0,
        babyId: member.id,
        babyName: member.babyName,
        briefSummary: '',
        coach: coachId,
        completion: '',
        consumeClass: 1,
        createUser: creator,
        customerName: member.customerName || '',
        details: originalDetails,
        heartRate: '',
        id: null,
        points: '',
        restTimes: '',
        suggestion: '',
        trainDate: date.format(`YYYY-MM-DD ${originalTrainTime}`),
        trainTimes: 1,
        updateUser: creator,
        teachingMode: member.deliveryType || 'VIP私教',
        courseKbn: "线下课程"
      };
      try {
        await apiClient.post('/sports-server-prod/taller.api.v1/prescription/add', payload);
      } catch (error) {
        message.error(`排课失败：${date.format('YYYY-MM-DD')}`);
        return;
      }
    }
    message.success('排课成功');
    setFutureSchedulingModalVisible(false);
    // 刷新数据
    loadWeekData();
  };

  const handleRowAddMember = (slotId) => {
    setActiveRowSlotId(slotId);
    setSelectingMemberEntryId(null); // 清除特定entry选择状态
    setActiveSelectorType((prev) => (prev === 'member' && activeRowSlotId === slotId ? null : 'member'));
  };

  // 为特定entry选择会员（用于空白明细行）
  const handleSelectMemberForEntry = (slotId, entryId) => {
    setActiveRowSlotId(slotId);
    setSelectingMemberEntryId(entryId);
    setActiveSelectorType('member');
  };

  const handleRowAddCourse = (slotId) => {
    setActiveRowSlotId(slotId);
    setActiveSelectorType((prev) => (prev === 'course' && activeRowSlotId === slotId ? null : 'course'));
  };

  const handleRowAddMyCourse = (slotId) => {
    setActiveRowSlotId(slotId);
    setMyCourseSelectorModalVisible(true);
  };

  // 添加空白明细行
  const handleAddEmptyEntry = (slotId) => {
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      let row = updatedRows[slotId];
      if (!row) {
        const slot = timeSlots.find((s) => s.slotId === slotId);
        row = {
          slotId,
          startTime: slot ? slot.startTime : dayjs(currentDate.format(`${formatDate} ${slotId}`)),
          period: DEFAULT_CONSUME,
          entries: [],
        };
        updatedRows[slotId] = row;
      }

      // 生成唯一ID
      const timestamp = Date.now();
      const newEntry = {
        id: `entry-${slotId}-${timestamp}`,
        members: [],
        courses: [],
        isExisting: false,
        updateFlag: 1,
        completeClass: '',
        teachingMode: '',
      };
      row.entries.push(newEntry);
      return { ...prev, [dateKey]: updatedRows };
    });
  };

  const getRowMembers = useCallback((slotId) => {
    const row = currentRows[slotId];
    if (!row || !Array.isArray(row.entries)) return [];
    const ids = [];
    row.entries.forEach((e) => {
      (e.members || []).forEach((m) => {
        if (!ids.includes(m.id)) ids.push(m.id);
      });
    });
    return ids;
  }, [currentRows]);

  const handleMemberSelect = (member) => {
    if (!activeRowSlotId || !member) return;
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      const slotId = activeRowSlotId;
      let row = updatedRows[slotId];
      if (!row) {
        const slot = timeSlots.find((s) => s.slotId === slotId);
        row = {
          slotId,
          startTime: slot ? slot.startTime : dayjs(currentDate.format(`${formatDate} ${slotId}`)),
          period: DEFAULT_CONSUME,
          entries: [],
        };
        updatedRows[slotId] = row;
      }

      const totalMembers = row.entries.reduce((acc, e) => acc + (e.members || []).length, 0);
      const exists = (row.entries[0]?.members || []).find((m) => m.id === member.id);

      if (exists) {
        // 检查是否存在已保存的记录（isExisting为true 或者有数字类型的ID）
        const hasSavedRecord = row.entries.some(entry => entry.isExisting === true || (entry.id && typeof entry.id === 'number'));
        
        if (hasSavedRecord) {
          // 如果存在已保存的记录，则显示确认弹框
          setRemoveMemberSlotId(slotId);
          setRemoveMemberId(member.id);
          setRemoveMember(member);
          setRemoveMemberModalVisible(true);
          return prev;
        } else {
          // 如果没有已保存的记录，直接从本地状态中移除
          row.entries.forEach((entry) => {
            entry.members = (entry.members || []).filter((m) => m.id !== member.id);
          });
          
          // 清理空的entry
          const filteredEntries = row.entries.filter(entry => (entry.members || []).length > 0);
          if (filteredEntries.length === 0) {
            delete updatedRows[slotId];
          } else {
            row.entries = filteredEntries;
          }
          
          return { ...prev, [dateKey]: updatedRows };
        }
      }

      // 移除4名会员限制
      // if (totalMembers >= 4) {
      //   message.warning('每个时间段最多选择4个会员');
      //   return prev;
      // }
      
      // 如果指定了特定entry，则添加到该entry
      if (selectingMemberEntryId) {
        const targetEntry = row.entries.find(e => e.id === selectingMemberEntryId);
        if (targetEntry) {
          targetEntry.members = targetEntry.members || [];
          if (!targetEntry.members.find((m) => m.id === member.id)) {
            targetEntry.members.push(member);
            targetEntry.updateFlag = 1;
          }
        }
        // 清除特定entry选择状态
        setSelectingMemberEntryId(null);
      } else {
        // ensure at least one entry
        if (!row.entries.length) {
          row.entries.push({
            id: `entry-${slotId}-0`,
            members: [],
            courses: [],
            isExisting: false,
            updateFlag: 0,
          });
        }
        row.entries[0].members = row.entries[0].members || [];
        if (!row.entries[0].members.find((m) => m.id === member.id)) {
          row.entries[0].members.push(member);
          row.entries[0].updateFlag = 1;
        }
      }
      return { ...prev, [dateKey]: updatedRows };
    });
  };

  const handleCoursesChange = (courses) => {
    if (!activeRowSlotId) return;
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      const slotId = activeRowSlotId;
      let row = updatedRows[slotId];
      if (!row) {
        const slot = timeSlots.find((s) => s.slotId === slotId);
        row = {
          slotId,
          startTime: slot ? slot.startTime : dayjs(currentDate.format(`${formatDate} ${slotId}`)),
          period: DEFAULT_CONSUME,
          entries: [],
        };
        updatedRows[slotId] = row;
      }

      const uniqueCourses = [];
      (courses || []).forEach((c) => {
        if (!uniqueCourses.find((u) => (u.seq && c.seq && u.seq === c.seq) || (u._key && c._key && u._key === c._key))) {
          uniqueCourses.push(c);
        }
      });

      // 如果指定了特定entry，则更新该entry的课程
      if (selectingCourseEntryId) {
        const targetEntry = row.entries.find(e => e.id === selectingCourseEntryId);
        if (targetEntry) {
          targetEntry.courses = uniqueCourses;
          targetEntry.updateFlag = 1;
        }
        // 清除特定entry选择状态
        setSelectingCourseEntryId(null);
      } else {
        // 默认更新第一个entry（兼容原有逻辑）
        if (!row.entries.length) {
          row.entries.push({
            id: `entry-${slotId}-0`,
            members: [],
            courses: [],
            isExisting: false,
            updateFlag: 0,
          });
        }
        row.entries[0].courses = uniqueCourses;
        row.entries[0].updateFlag = 1;
      }
      return { ...prev, [dateKey]: updatedRows };
    });
  };

  // 新增：处理我的课程选择
  const handleMyCourseSelect = (selectedCourse) => {
    if (!activeRowSlotId || !selectedCourse) return;
    
    // 提取选中课程的关联动作
    const courseActions = [];
    
    if (selectedCourse.relateActions && Array.isArray(selectedCourse.relateActions)) {
      // 如果课程有关联动作数组
      selectedCourse.relateActions.forEach(action => {
        courseActions.push({
          _key: `${action.seq}-${action.eventTitle}`,
          seq: action.seq,
          eventTitle: action.eventTitle,
          courseContext: action.courseContext || '',
          courseDesc: selectedCourse.courseDesc, // 保留课程描述
          coursePrice: selectedCourse.coursePrice, // 保留课程价格
        });
      });
    } else if (selectedCourse.relateAction && selectedCourse.relateActionShow) {
      // 如果使用新的relateAction和relateActionShow字段
      const seqArray = selectedCourse.relateAction ? selectedCourse.relateAction.split(',') : [];
      const eventTitleArray = selectedCourse.relateActionShow ? selectedCourse.relateActionShow.split(',') : [];
      
      seqArray.forEach((seq, index) => {
        const eventTitle = eventTitleArray[index];
        if (eventTitle) {
          courseActions.push({
            _key: `${seq}-${eventTitle}`,
            seq: seq,
            eventTitle: eventTitle,
            courseContext: '', // 从关联动作中可能没有上下文信息
            courseDesc: selectedCourse.courseDesc, // 保留课程描述
            coursePrice: selectedCourse.coursePrice, // 保留课程价格
          });
        }
      });
    }

    // 更新当前行的课程 - 清除原有课程，替换为新选择的课程动作
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      const slotId = activeRowSlotId;
      let row = updatedRows[slotId];
      if (!row) {
        const slot = timeSlots.find((s) => s.slotId === slotId);
        row = {
          slotId,
          startTime: slot ? slot.startTime : dayjs(currentDate.format(`${formatDate} ${slotId}`)),
          period: DEFAULT_CONSUME,
          entries: [],
        };
        updatedRows[slotId] = row;
      }

      if (!row.entries.length) {
        row.entries.push({
          id: `entry-${slotId}-0`,
          members: [],
          courses: [],
          isExisting: false,
          updateFlag: 0,
        });
      }

      // 清除原有课程，替换为新选择的课程动作
      row.entries[0].courses = courseActions;
      row.entries[0].updateFlag = 1;
      return { ...prev, [dateKey]: updatedRows };
    });

    // 关闭模态框
    setMyCourseSelectorModalVisible(false);
  };

  const handlePeriodChange = (slotId, value) => {
    const period = Number(value) || DEFAULT_CONSUME;
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      let row = updatedRows[slotId];
      if (!row) {
        const slot = timeSlots.find((s) => s.slotId === slotId);
        row = {
          slotId,
          startTime: slot ? slot.startTime : dayjs(currentDate.format(`${formatDate} ${slotId}`)),
          period,
          entries: [],
        };
        updatedRows[slotId] = row;
      } else {
        row.period = period;
        if (row.entries.length > 0) {
          row.entries[0].updateFlag = 1;
        }
      }
      return { ...prev, [dateKey]: updatedRows };
    });
  };

  const handleDeleteEntry = (slotId, entryId) => {
    setDeleteSlotId(slotId);
    setDeleteEntryId(entryId);
    setDeleteModalVisible(true);
  };

    const loadWeekData = useCallback(async () => {
    if (!apiClient) return;
    setPageLoading(true);
    try {
      const payload = {
        coach: coach,
        pageNo: 1,
        pageSize: 999,
        searchDate: currentDate.format(formatDate),
        courseKbn: activeTab === 'offline' ? '线下课程' : '家庭训练',
      };
      const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/pageList', payload);
      if (resp?.data?.code !== 200) {
        throw new Error(resp?.data?.msg || '获取排课数据失败');
      }
      const list = resp.data.data?.list || [];
      const allRows = mapPageListToRows(list);
      setScheduleRows(allRows);
    } catch (e) {
      message.error(e?.message || '获取排课数据失败');
    } finally {
      setPageLoading(false);
    }
    }, [apiClient, coach, currentDate, activeTab]);

  useEffect(() => {
    loadWeekData();
  }, [currentDate, coach, activeTab]);

  const handleSave = async () => {
    if (!apiClient) return;
    // 构建保存任务 - 按会员和时间分组
    const tasks = [];
    Object.keys(scheduleRows).forEach((dateKey) => {
      const rows = scheduleRows[dateKey];
      Object.values(rows).forEach((row) => {
        if (!row || !Array.isArray(row.entries) || !row.entries.length) return;
        const period = row.period || DEFAULT_CONSUME;
        row.entries.forEach((entry) => {
          const { members = [], courses = [] } = entry;
          if (!members.length || !courses.length) return;
          
          // 按会员分组课程
          members.forEach((member) => {
            // 查找或创建该会员在此时间段的任务
            let task = tasks.find(t => t.member.id === member.id && t.dateTime.isSame(row.startTime, 'minute'));
            if (!task) {
              task = {
                member,
                dateTime: row.startTime,
                period,
                courses: [],
                entryId: entry.id,
              };
              tasks.push(task);
            }
            // 添加课程到该任务
            courses.forEach(course => {
              if (!task.courses.some(c => 
                (c.seq && c.seq === course.seq) || 
                (c._key && c._key === course._key)
              )) {
                task.courses.push(course);
              }
            });
          });
        });
      });
    });

    if (!tasks.length) {
      message.info('当前没有可保存的排课');
      return;
    }

    const total = tasks.length;
    let completed = 0;
    const errors = [];

    setSaving(true);
    setSaveProgress({ completed: 0, total, currentMemberName: '' });

    const creator = user?.name || '';

    for (const task of tasks) {
      const memberName = task.member.babyName || '';
      const payload = {
        ...buildMemberPayload({
          member: task.member,
          course: null, // 不在这里设置单个课程，因为课程会在details中设置
          dateTime: task.dateTime,
          period: task.period,
          creator,
          coach: coach,
          id: task.entryId,
          activeTab: activeTab,
        }),
        // 用任务中的课程列表替换details
        details: task.courses.map((course) => ({
          sportsItem: course.eventTitle,
          sportsContent: course.courseContext || '',
        })),
        // 添加课程区分参数
        courseKbn: activeTab === 'offline' ? '线下课程' : '家庭训练',
      };
      
      try {
        const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/add', payload);

        if (!(resp?.data && resp.data.code === 200)) {
          throw new Error(resp?.data?.msg || '保存失败');
        }
      } catch (e) {
        errors.push({
          memberName: task.member.babyName,
          time: task.dateTime.format('YYYY-MM-DD HH:mm'),
          courseTitle: task.courses.map(c => c.eventTitle).join(','),
          msg: e?.message || '保存失败',
        });
      } finally {
        completed += 1;
        setSaveProgress({ completed, total, currentMemberName: memberName });
      }
    }

    setSaving(false);
    if (errors.length) {
      const brief = errors.slice(0, 5).map((er) => `${er.time} ${er.memberName} ${er.courseTitle || ''}: ${er.msg}`).join('\n');
      message.error(`部分排课保存失败，共 ${errors.length} 条。前5条如下：\n${brief}`);
    } else {
      message.success('排课保存成功');
      // 重新加载本周数据
      loadWeekData();
    }
  };

  const handleCopySchedule = () => {
    if (!copyTargetDate) {
      message.warning('请选择目标日期');
      return;
    }
    const sourceRows = scheduleRows[currentDate.format(formatDate)] || {};
    const targetKey = copyTargetDate.format(formatDate);
    let targetRows = { ...scheduleRows[targetKey] } || {};
    if (copyMode === 'overwrite') {
      targetRows = { ...sourceRows };
    } else {
      // merge
      Object.keys(sourceRows).forEach(slotId => {
        if (!targetRows[slotId]) {
          targetRows[slotId] = { ...sourceRows[slotId] };
        } else {
          // for simplicity, overwrite the row
          targetRows[slotId] = { ...sourceRows[slotId] };
        }
      });
    }
    // adjust startTime for target date
    Object.keys(targetRows).forEach(slotId => {
      const row = targetRows[slotId];
      const [hour, minute] = slotId.split(':').map(Number);
      row.startTime = copyTargetDate.hour(hour).minute(minute).second(0);
    });
    setScheduleRows(prev => ({ ...prev, [targetKey]: targetRows }));
    message.success('排课复制成功');
    setCopyModalVisible(false);
  };

  // 复制课程函数 - 参考MemberList.js的接口
  const handleCopyCourse = async (slotId) => {
    const row = currentRows[slotId];
    if (!row || !row.entries || row.entries.length === 0) return;
    
    const firstEntry = row.entries[0];
    if (!firstEntry || !firstEntry.members || firstEntry.members.length !== 1) return;
    
    const member = firstEntry.members[0];
    
    try {
      // 首先获取最近的课程历史
      const resp = await apiClient.get('/sports-server-prod/taller.api.v1/prescriptionDetail/getlistByBabyId', {
        params: { babyId: member.id }
      });

      if (resp.data && resp.data.code === 200) {
        const list = resp.data.data?.list || [];
        if (list.length > 0) {
          // 获取最新的课程记录
          const latestCourse = list[0]; // 假设列表按时间倒序排列
          const newCourses = [{
            _key: `${latestCourse.sportsItem || ''}-${latestCourse.sportsContent || ''}`,
            courseKbn: latestCourse.sportsItem,
            eventTitle: latestCourse.sportsItem?.split('/')[1] || latestCourse.sportsItem,
            courseContext: latestCourse.sportsContent,
          }];
          
          // 更新当前行的课程
          setScheduleRows((prev) => {
            const dateKey = currentDate.format(formatDate);
            const updatedRows = { ...prev[dateKey] };
            const updatedSlotId = slotId;
            let updatedRow = updatedRows[updatedSlotId];
            if (!updatedRow) return;
            
            if (!updatedRow.entries.length) {
              updatedRow.entries.push({
                id: `entry-${updatedSlotId}-0`,
                members: [...firstEntry.members],
                courses: [],
                isExisting: false,
                updateFlag: 0,
              });
            }
            updatedRow.entries[0].courses = newCourses;
            updatedRow.entries[0].updateFlag = 1;
            return { ...prev, [dateKey]: updatedRows };
          });
          
          message.success('课程复制成功');
        } else {
          message.info('该会员暂无历史课程记录');
        }
      } else {
        message.error(resp.data?.msg || '获取历史课程失败');
      }
    } catch (e) {
      console.error('复制课程失败:', e);
      message.error('复制课程失败');
    } finally {
      setCopyCourseModal(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteSlotId || !deleteEntryId) return;
    if (typeof deleteEntryId === 'number') {
      try {
        const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/delete?id=' + deleteEntryId);
        if (resp?.data?.code === 200) {
          message.success('删除成功');
        } else {
          message.error(resp?.data?.msg || '删除失败');
          return;
        }
      } catch (e) {
        message.error('删除失败');
        return;
      }
    }
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      const row = updatedRows[deleteSlotId];
      if (!row) return prev;
      row.entries = row.entries.filter((e) => e.id !== deleteEntryId);
      if (!row.entries.length) {
        delete updatedRows[deleteSlotId];
      }
      return { ...prev, [dateKey]: updatedRows };
    });
    setDeleteModalVisible(false);
  };

    const handleRemoveMemberConfirm = async () => {
    if (!removeMemberSlotId || !removeMemberId) return;
    
    const row = currentRows[removeMemberSlotId];
    if (row) {
      const entry = row.entries.find(e => e.members.some(m => m.id === removeMemberId));
      
      // 如果entry有ID（表示已保存到数据库），则调用API删除
      if (entry && typeof entry.id === 'number') {
        try {
          const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/delete?id=' + entry.id);
          if (resp?.data?.code === 200) {
            message.success('删除成功');
          } else {
            message.error(resp?.data?.msg || '删除失败');
            return;
          }
        } catch (e) {
          message.error('删除失败');
          return;
        }
      }
      // 如果entry没有ID（表示只是临时添加的记录），则跳过API调用，只更新本地状态
    }
    
    // 从本地状态移除会员
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      const row = updatedRows[removeMemberSlotId];
      if (!row) return prev;
      
      row.entries.forEach((entry) => {
        entry.members = (entry.members || []).filter((m) => m.id !== removeMemberId);
      });
      
      // 清理空的entry
      const filteredEntries = row.entries.filter(entry => (entry.members || []).length > 0);
      if (filteredEntries.length === 0) {
        delete updatedRows[removeMemberSlotId];
      } else {
        row.entries = filteredEntries;
      }
      
      return { ...prev, [dateKey]: updatedRows };
    });
    
    setRemoveMemberModalVisible(false);
    message.success('移除会员成功');
  };

  const handleSingleSave = (slotId, entryId) => {
    setSingleSaveSlotId(slotId);
    setSingleSaveEntryId(entryId);
    setSingleSaveModalVisible(true);
  };

  const handleSingleSaveConfirm = async () => {
    if (!singleSaveSlotId || !singleSaveEntryId) return;
    const row = currentRows[singleSaveSlotId];
    if (!row) return;
    const entry = row.entries.find(e => e.id === singleSaveEntryId);
    if (!entry) return;

    const { members = [], courses = [] } = entry;
    if (!members.length || !courses.length) {
      message.warning('会员或课程不能为空');
      return;
    }

    const period = row.period || DEFAULT_CONSUME;
    const creator = user?.name || '';

    // 构建任务
    const tasks = [];
    members.forEach((member) => {
      const task = {
        member,
        dateTime: row.startTime,
        period,
        courses: courses,
        entryId: entry.id,
      };
      tasks.push(task);
    });

    const errors = [];
    for (const task of tasks) {
      // 判断该会员是否是新添加的：如果 entry 有数字ID（已保存），但会员不在原始保存的会员列表中，则是新添加的
      const isEntrySaved = typeof entry.id === 'number';
      const isNewMember = isEntrySaved && entry.originalMemberIds && !entry.originalMemberIds.includes(task.member.id);
      
      // 如果是新添加的会员，去掉ID，让后端创建新记录
      const submitId = isNewMember ? null : task.entryId;
      
      const payload = {
        ...buildMemberPayload({
          member: task.member,
          course: null,
          dateTime: task.dateTime,
          period: task.period,
          creator,
          coach: coach,
          id: submitId,
          activeTab: activeTab,
        }),
        details: task.courses.map((course) => ({
          sportsItem: course.eventTitle,
          sportsContent: course.courseContext || '',
        })),
        // 添加课程区分参数
        courseKbn: activeTab === 'offline' ? '线下课程' : '家庭训练',
      };

      try {
        const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/add', payload);
        if (!(resp?.data && resp.data.code === 200)) {
          throw new Error(resp?.data?.msg || '保存失败');
        }
      } catch (e) {
        errors.push({
          memberName: task.member.babyName,
          msg: e?.message || '保存失败',
        });
      }
    }

    if (errors.length) {
      message.error(`保存失败：${errors.map(er => `${er.memberName}: ${er.msg}`).join(', ')}`);
    } else {
      message.success('保存成功');
      // 重置 updateFlag
      setScheduleRows((prev) => {
        const dateKey = currentDate.format(formatDate);
        const updatedRows = { ...prev[dateKey] };
        const updatedRow = updatedRows[singleSaveSlotId];
        if (updatedRow) {
          const updatedEntry = updatedRow.entries.find(e => e.id === singleSaveEntryId);
          if (updatedEntry) {
            updatedEntry.updateFlag = 0;
          }
        }
        return { ...prev, [dateKey]: updatedRows };
      });
      loadWeekData();
    }
    setSingleSaveModalVisible(false);
  };

  // 处理变更时间
  const handleChangeTime = (slotId, entryId) => {
    setChangeTimeSlotId(slotId);
    setChangeTimeEntryId(entryId);
    setChangeTimeModalVisible(true);
    // 默认选择当前日期和时间
    setSelectedNewDate(currentDate);
    setSelectedNewTime(timeSlots.find(slot => slot.slotId === slotId)?.startTime || null);
  };

  // 确认变更时间
  const handleChangeTimeConfirm = async () => {
    if (!changeTimeSlotId || !changeTimeEntryId || !selectedNewDate || !selectedNewTime) {
      message.warning('请选择新的日期和时间');
      return;
    }

    // 创建新的时间信息
    const newDateTime = selectedNewDate.hour(selectedNewTime.hour()).minute(selectedNewTime.minute());
    const newSlotId = newDateTime.format(formatTime);
    
    // 更新排课数据中的时间和slotId
    setScheduleRows((prev) => {
      const dateKey = currentDate.format(formatDate);
      const updatedRows = { ...prev[dateKey] };
      
      // 找到需要更新的entry
      const oldRow = updatedRows[changeTimeSlotId];
      if (!oldRow) return prev;
      
      const entryIndex = oldRow.entries.findIndex(e => e.id === changeTimeEntryId);
      if (entryIndex === -1) return prev;
      
      // 获取entry对象
      const entry = oldRow.entries[entryIndex];
      
      // 如果新时间段不存在，创建它
      if (!updatedRows[newSlotId]) {
        updatedRows[newSlotId] = {
          slotId: newSlotId,
          startTime: newDateTime,
          period: oldRow.period || DEFAULT_CONSUME,
          entries: [],
        };
      }
      
      // 更新entry的时间信息并将其移动到新时间段
      const updatedEntry = { ...entry };
      updatedEntry.updateFlag = 1; // 标记为需要更新
      updatedRows[newSlotId].entries.push(updatedEntry);
      
      // 从原时间段移除entry
      oldRow.entries.splice(entryIndex, 1);
      if (oldRow.entries.length === 0) {
        delete updatedRows[changeTimeSlotId];
      }
      
      return { ...prev, [dateKey]: updatedRows };
    });
    
    // 调用保存函数，使用新的slotId
    handleSingleSave(newSlotId, changeTimeEntryId);
    
    setChangeTimeModalVisible(false);
  };

  // 处理AI教练生成计划
  const handleGenerateAIPlan = async (slotId, entryId) => {
    // 设置防重复点击状态 - 使用entryId作为loading标识
    setAiCoachLoading(prev => ({ ...prev, [entryId]: true }));
    
    try {
      const row = currentRows[slotId];
      if (!row || !row.entries || row.entries.length === 0) {
        message.warning('请先选择会员');
        return;
      }
      
      // 根据entryId查找对应的entry
      const targetEntry = row.entries.find(e => e.id === entryId);
      if (!targetEntry) {
        message.warning('未找到对应的排课记录');
        return;
      }
      
      if (!targetEntry.members || targetEntry.members.length === 0) {
        message.warning('请先选择会员');
        return;
      }
      
      // 对于已保存的记录，使用第一个会员（因为已拆分为虚拟行，每个entry只有一个会员）
      const member = targetEntry.members[0];
      if (!member) {
        message.warning('请先选择会员');
        return;
      }
      
      // 准备请求参数
      const customerId = member.customerId || (storedUser.customerId ? storedUser.customerId.split('-')[0] : '');
      const payload = {
        customerId: customerId, // 当前会员的机构ID
        babyId: member.id, // 当前会员id
        trainDate: currentDate.format('YYYY-MM-DD') // 当前训练日
      };
      
      // 调用后端接口
      const response = await apiClient.post('/sports-server-prod/taller.api.v1/aicoach/generatePlan', payload);
      
      if (response.data && response.data.code === 200) {
        const planData = response.data.data;
        
        if (!planData || planData.trim() === '') {
          message.info('未匹配到合适计划，请继续学习');
          return;
        }
        
        // 解析返回的数据
        const planItems = planData.split(';').filter(item => item.trim() !== '');
        
        const courses = planItems.map((item, index) => {
          const parts = item.split('-');
          return {
            _key: `${parts[0] || `AI课程${index+1}`}-${parts[1] || ''}`,
            eventTitle: parts[0] || `AI课程${index+1}`,
            courseContext: parts.length > 1 ? parts[1] : '',
          };
        });
        
        // 更新指定entry的课程
        setScheduleRows((prev) => {
          const dateKey = currentDate.format(formatDate);
          const updatedRows = { ...prev[dateKey] };
          const updatedRow = updatedRows[slotId];
          if (!updatedRow) return prev;
          
          // 查找并更新指定的entry
          const entryToUpdate = updatedRow.entries.find(e => e.id === entryId);
          if (entryToUpdate) {
            entryToUpdate.courses = courses;
            entryToUpdate.updateFlag = 1;
          }
          return { ...prev, [dateKey]: updatedRows };
        });
        
        message.success('AI已为您制定训练计划');
      } else {
        message.error(response.data?.msg || '生成计划失败');
      }
    } catch (error) {
      console.error('AI教练生成计划失败:', error);
      message.error(error.response?.data?.msg || '生成计划失败');
    } finally {
      // 重置防重复点击状态
      setAiCoachLoading(prev => ({ ...prev, [entryId]: false }));
    }
  };

  const tabItems = [
    {
      key: 'offline',
      label: '线下课程',
    },
    {
      key: 'home',
      label: '家庭训练',
    },
  ];

  return (
    <div className="schedule-course">
      <Card style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={[4, 0]}>
          <Col xs={24} md={3}>
            <DatePicker value={currentDate} onChange={handleSelectDate} />
          </Col>
          <Col xs={24} md={2}>
            <Button type="default" icon={<SearchOutlined />} onClick={loadWeekData}>
              搜索
            </Button>
          </Col>
          <Col xs={24} md={16} style={{ textAlign: 'right' }}>
            <Space size={8}>
              <Button onClick={handlePrintSchedule} loading={printing}>
                打印排课表
              </Button>
              <Button type="primary" onClick={handleSave} loading={saving}>
                保存排课
              </Button>
            </Space>
          </Col>
        </Row>
        <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} tabBarGutter={16} style={{ marginTop: 16 }} />
         {saving && (
           <Row style={{ marginTop: 16 }}>
             <Col span={24}>
               <Space direction="vertical" style={{ width: '100%' }}>
                 <Text>
                   正在保存排课：{saveProgress.currentMemberName} ({saveProgress.completed}/{saveProgress.total})
                 </Text>
                 <Progress percent={Math.round((saveProgress.completed / (saveProgress.total || 1)) * 100)} />
               </Space>
             </Col>
           </Row>
         )}
       </Card>

       <Card ref={schedulePrintRef}>
         {pageLoading ? (
           <Spin />
         ) : (
           <div ref={scheduleScrollRef} style={{ maxHeight: 600, overflowY: 'auto' }}>
             {timeSlots.map((slot) => {
               const row = currentRows[slot.slotId];
               const entries = row?.entries || [];
               const membersCount = getRowMembers(slot.slotId).length;
               const hasSingleMember = membersCount === 1;
               return (
                 <Row
                   key={slot.slotId}
                   align="middle"
                   style={{
                     borderBottom: '1px solid #f0f0f0',
                     padding: '8px 0',
                   }}
                   gutter={16}
                 >
                   <Col span={3}>
                     <Text strong>{slot.label}</Text>
                   </Col>
                   <Col span={12}>
                     <Space wrap>
                       {/* 已保存的entries（有数字ID的）- 按会员拆分为虚拟行 */}
                       {entries
                         .filter((entry) => typeof entry.id === 'number')
                         .flatMap((entry) => {
                           // 将每个会员拆分为独立的虚拟行
                           const members = entry.members || [];
                           if (members.length === 0) return [];
                           return members.map((member) => ({
                             ...entry,
                             virtualKey: `${entry.id}-${member.id}`,
                             singleMember: member,
                           }));
                         })
                         .map((virtualEntry) => (
                         <Card
                           key={virtualEntry.virtualKey}
                           size="small"
                           style={{ minWidth: 220 }}
                           bodyStyle={{ padding: 8 }}
                         >
                           <Space direction="vertical" size={4} style={{ width: '100%' }}>
                             <div>
                               <Text type="secondary">会员：</Text>
                               {(() => {
                                 const m = virtualEntry.singleMember;
                                 const teachingMode = virtualEntry.teachingMode || m.deliveryType || '';
                                 return (
                                   <div key={m.id}>
                                     <Tag color={
                                       getDisplayDeliveryType(teachingMode) === 'SVIP专属' || teachingMode === '一对一' ? 'gold' :
                                       getDisplayDeliveryType(teachingMode) === 'VIP私教' || teachingMode === '一对二' ? 'blue' :
                                       getDisplayDeliveryType(teachingMode) === '私教小团' || teachingMode === '一对多' ? 'green' : 'default'
                                     }>
                                       {getDisplayDeliveryType(teachingMode) === 'SVIP专属' || teachingMode === '一对一' ? '👑 ' : 
                                        getDisplayDeliveryType(teachingMode) === 'VIP私教' || teachingMode === '一对二' ? '⭐ ' : 
                                        getDisplayDeliveryType(teachingMode) === '私教小团' || teachingMode === '一对多' ? '👥 ' : ''}
                                       {m.babyName} {teachingMode ? `(${getDisplayDeliveryType(teachingMode)})` : ''}
                                     </Tag>
                                   </div>
                                 );
                               })()}
                             </div>
                             <div>
                               <Text type="secondary">课程：</Text>
                               {(virtualEntry.courses || []).map((c) => (
                                 <Tag key={c.seq || c._key || c.eventTitle}>{c.eventTitle || c.sportsItem}</Tag>
                               ))}
                               {!virtualEntry.courses?.length && <Text type="secondary">未选择</Text>}
                             </div>
                             <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                               <Space>
                                 {activeTab !== 'home' && (
                                   <>
                                     <Text type="secondary">本节耗课：</Text>
                                     <InputNumber
                                       min={0.5}
                                       step={0.5}
                                       precision={1}
                                       value={row?.period || (activeTab === 'home' ? 0 : DEFAULT_CONSUME)}
                                       onChange={(val) => handlePeriodChange(slot.slotId, val)}
                                       style={{ width: 80 }}
                                     />
                                   </>
                                 )}
                               </Space>
                             </Space>
                             <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 8 }}>
                               {virtualEntry.updateFlag === 1 && virtualEntry.completeClass !== '1' && (
                                 <Button type="primary" size="small" onClick={() => handleSingleSave(slot.slotId, virtualEntry.id)}>
                                   保存
                                 </Button>
                               )}
                               {/* 变更时间按钮 - 仅当有ID且未结课时显示 */}
                               {typeof virtualEntry.id === 'number' && virtualEntry.completeClass !== '1' && (
                                   <Button type="link" size="small" onClick={() => handleChangeTime(slot.slotId, virtualEntry.id)}>
                                     变更时间
                                   </Button>
                               )}
                               {/* 只有当entry有ID（训练记录已保存）时才显示训练反馈按钮 */}
                               {typeof virtualEntry.id === 'number' && (
                                 <Button type="link" size="small" onClick={() => handleOpenTrainingFeedback(virtualEntry)}>
                                   训练反馈
                                 </Button>
                               )}
                               {/* 未来排课按钮 */}
                               {typeof virtualEntry.id === 'number' && (
                                 <Button type="link" size="small" onClick={() => handleOpenFutureScheduling(virtualEntry)}>
                                   未来排课
                                 </Button>
                               )}
                               <Button type="link" size="small" danger onClick={() => {
                                 setDeleteSlotId(slot.slotId);
                                 setDeleteEntryId(virtualEntry.id);
                                 setDeleteModalVisible(true);
                               }}>
                                 删除
                               </Button>
                               {/* 复制最近内容按钮 - 单个会员时显示 */}
                               {virtualEntry.completeClass !== '1' && (
                                 <Tooltip title="复制最近内容">
                                   <Button
                                     type="link"
                                     size="small"
                                     icon={<CopyOutlined />}
                                     onClick={() => {
                                       setCopySlotId(slot.slotId);
                                       setCopyCourseModal(true);
                                     }}
                                   />
                                 </Tooltip>
                               )}
                               {/* AI 教练按钮 */}
                               {activeTab !== 'home' && (
                                 <Tooltip title="AI 教练">
                                   <Button
                                     type="link"
                                     size="small"
                                     loading={aiCoachLoading[virtualEntry.id] || false}
                                     onClick={() => handleGenerateAIPlan(slot.slotId, virtualEntry.id)}
                                     style={{ 
                                       backgroundColor: '#1890ff', 
                                       borderColor: '#1890ff', 
                                       color: 'white',
                                       minWidth: '32px',
                                       height: '24px',
                                       lineHeight: '24px',
                                       textAlign: 'center',
                                       fontSize: '12px',
                                       fontWeight: 'bold'
                                     }}
                                   >
                                     AI
                                   </Button>
                                 </Tooltip>
                               )}
                               {/* 从动作资源库选择按钮 */}
                               <Tooltip title="从动作资源库选择">
                                 <Button
                                   type="link"
                                   size="small"
                                   icon={<ReadOutlined />}
                                   onClick={() => {
                                     setActiveRowSlotId(slot.slotId);
                                     setSelectingCourseEntryId(virtualEntry.id);
                                     setActiveSelectorType('course');
                                   }}
                                 />
                               </Tooltip>
                             </Space>
                           </Space>
                         </Card>
                       ))}
                       
                       {/* 新添加的entries（没有数字ID的，即待保存的）*/}
                      {entries
                        .filter((entry) => typeof entry.id !== 'number')
                        .map((entry) => (
                        <Card
                          key={entry.id}
                          size="small"
                          style={{ minWidth: 220, borderColor: '#1890ff' }}
                          bodyStyle={{ padding: 8 }}
                        >
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <div>
                              <Text type="secondary">会员：</Text>
                              {(entry.members || []).map((m) => {
                                // 优先使用entry.teachingMode，如果没有则使用member.deliveryType
                                const teachingMode = entry.teachingMode || m.deliveryType || '';
                                return (
                                <div key={m.id}>
                                  <Tag color={
                                    getDisplayDeliveryType(teachingMode) === 'SVIP专属' || teachingMode === '一对一' ? 'gold' :
                                    getDisplayDeliveryType(teachingMode) === 'VIP私教' || teachingMode === '一对二' ? 'blue' :
                                    getDisplayDeliveryType(teachingMode) === '私教小团' || teachingMode === '一对多' ? 'green' : 'default'
                                  }>
                                    {getDisplayDeliveryType(teachingMode) === 'SVIP专属' || teachingMode === '一对一' ? '👑 ' : 
                                     getDisplayDeliveryType(teachingMode) === 'VIP私教' || teachingMode === '一对二' ? '⭐ ' : 
                                     getDisplayDeliveryType(teachingMode) === '私教小团' || teachingMode === '一对多' ? '👥 ' : ''}
                                    {m.babyName} {teachingMode ? `(${getDisplayDeliveryType(teachingMode)})` : ''}
                                  </Tag>
                                </div>
                                );
                              })}
                              {!entry.members?.length && <Text type="secondary">未选择</Text>}
                            </div>
                            <div>
                              <Text type="secondary">课程：</Text>
                              {(entry.courses || []).map((c) => (
                                <Tag key={c.seq || c._key || c.eventTitle}>{c.eventTitle || c.sportsItem}</Tag>
                              ))}
                              {!entry.courses?.length && <Text type="secondary">未选择</Text>}
                            </div>
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                              <Space>
                                {activeTab !== 'home' && (
                                  <>
                                    <Text type="secondary">本节耗课：</Text>
                                    <InputNumber
                                      min={0.5}
                                      step={0.5}
                                      precision={1}
                                      value={row?.period || (activeTab === 'home' ? 0 : DEFAULT_CONSUME)}
                                      onChange={(val) => handlePeriodChange(slot.slotId, val)}
                                      style={{ width: 80 }}
                                    />
                                  </>
                                )}
                              </Space>
                            </Space>
                            <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 8 }}>
                              {/* 选择会员按钮 - 空白明细行显示 */}
                              {(!entry.members || entry.members.length === 0) && (
                                <Button 
                                  type="primary" 
                                  size="small" 
                                  onClick={() => handleSelectMemberForEntry(slot.slotId, entry.id)}
                                >
                                  选择会员
                                </Button>
                              )}
                              {entry.updateFlag === 1 && entry.completeClass !== '1' && (
                                <Button type="primary" size="small" onClick={() => handleSingleSave(slot.slotId, entry.id)}>
                                  保存
                                </Button>
                              )}
                              <Button type="link" size="small" danger onClick={() => {
                                setDeleteSlotId(slot.slotId);
                                setDeleteEntryId(entry.id);
                                setDeleteModalVisible(true);
                              }}>
                                删除
                              </Button>
                              {/* 复制最近内容按钮 - 仅当有单个会员且未结课时显示 */}
                              {entry.members && entry.members.length === 1 && entry.completeClass !== '1' && (
                                <Tooltip title="复制最近内容">
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() => {
                                      setCopySlotId(slot.slotId);
                                      setCopyCourseModal(true);
                                    }}
                                  />
                                </Tooltip>
                              )}
                              {/* AI 教练按钮 - 仅在线下课程tab且有会员时显示 */}
                              {activeTab !== 'home' && entry.members && entry.members.length > 0 && (
                                <Tooltip title="AI 教练">
                                  <Button
                                    type="link"
                                    size="small"
                                    loading={aiCoachLoading[entry.id] || false}
                                    onClick={() => handleGenerateAIPlan(slot.slotId, entry.id)}
                                    style={{ 
                                      backgroundColor: '#1890ff', 
                                      borderColor: '#1890ff', 
                                      color: 'white',
                                      minWidth: '32px',
                                      height: '24px',
                                      lineHeight: '24px',
                                      textAlign: 'center',
                                      fontSize: '12px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    AI
                                  </Button>
                                </Tooltip>
                              )}
                              {/* 从动作资源库选择按钮 - 仅当有会员时显示 */}
                              {entry.members && entry.members.length > 0 && (
                                <Tooltip title="从动作资源库选择">
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<ReadOutlined />}
                                    onClick={() => {
                                      setActiveRowSlotId(slot.slotId);
                                      setSelectingCourseEntryId(entry.id);
                                      setActiveSelectorType('course');
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Space>
                          </Space>
                        </Card>
                      ))}

                      {!entries.length && <Text type="secondary">暂无排课</Text>}
                     </Space>
                   </Col>
                   <Col span={9}>
                    <Space size="middle">
                      <Tooltip title={`选择会员${membersCount ? ` (${membersCount})` : ''}`}>
                        <Badge count={membersCount ? `${membersCount}` : null} size="small">
                          <Button
                            shape="circle"
                            type="primary"
                            icon={<UserAddOutlined />}
                            onClick={() => handleRowAddMember(slot.slotId)}
                          />
                        </Badge>
                      </Tooltip>
                      {/*<Tooltip title="添加空白明细">*/}
                      {/*  <Button*/}
                      {/*    shape="circle"*/}
                      {/*    icon={<PlusOutlined />}*/}
                      {/*    onClick={() => handleAddEmptyEntry(slot.slotId)}*/}
                      {/*  />*/}
                      {/*</Tooltip>*/}
                      {/*<Tooltip title="从我的课程库选择">*/}
                      {/*  <Button*/}
                      {/*    shape="circle"*/}
                      {/*    icon={<BookOutlined />}*/}
                      {/*    onClick={() => handleRowAddMyCourse(slot.slotId)}*/}
                      {/*  />*/}
                      {/*</Tooltip>*/}
                      {/* 复制最近内容按钮移到每个entry内部 */}
                      {/* AI 教练按钮移到每个entry内部 */}
                    </Space>
                   </Col>
                 </Row>
               );
             })}
           </div>
         )}
       </Card>

       <Drawer
         title={activeSelectorType === 'member' ? '选择会员' : activeSelectorType === 'course' ? '从动作资源库选择' : ''}
         placement="right"
         width={480}
         onClose={closeDrawer}
         open={!!activeSelectorType}
       >
         {activeSelectorType === 'member' && activeRowSlotId && (
           <MemberSelector
             onSelect={handleMemberSelect}
             selectedMemberIds={getRowMembers(activeRowSlotId)}
           />
         )}
         {activeSelectorType === 'course' && activeRowSlotId && (
           (() => {
             // 使用 useMemo 确保引用稳定性
             const currentEntry = currentRows[activeRowSlotId]?.entries?.[0];
             
             let selectedCourseSeqs = [];
             if (currentEntry && currentEntry.courses && currentEntry.courses.length > 0) {
               selectedCourseSeqs = currentEntry.courses
                 .map((c) => c.seq)
                 .filter(Boolean);
             }
             return (
               <CourseSelector
                 onCoursesSelect={handleCoursesChange}
                 selectedCourseSeqs={selectedCourseSeqs}
                 defaultCourseType={activeTab === 'offline' ? '线下课程' : '家庭训练'}
                 disabled={false}
               />
             );
           })()
         )}
       </Drawer>

       {/* 我的课程选择器模态框 */}
       <MyCourseSelector
         open={myCourseSelectorModalVisible}
         onCancel={() => setMyCourseSelectorModalVisible(false)}
         onCourseSelect={handleMyCourseSelect}
       />

       <Modal
         title="复制排课"
         open={copyModalVisible}
         onCancel={() => setCopyModalVisible(false)}
         onOk={handleCopySchedule}
         okText="复制"
         cancelText="取消"
       >
         <Space direction="vertical">
           <div>
             <Text>选择目标日期：</Text>
             <Select
               style={{ width: 120 }}
               value={copyTargetDate ? copyTargetDate.format(formatDate) : undefined}
               onChange={(value) => setCopyTargetDate(dayjs(value))}
             >
               {weekDays.filter((d) => !d.date.isSame(currentDate, 'day')).map((d) => (
                 <Select.Option key={d.date.format(formatDate)} value={d.date.format(formatDate)}>
                   {d.date.format('MM-DD')} {d.label}
                 </Select.Option>
               ))}
             </Select>
           </div>
           <Radio.Group value={copyMode} onChange={(e) => setCopyMode(e.target.value)}>
             <Radio value="overwrite">覆盖</Radio>
             <Radio value="merge">合并</Radio>
           </Radio.Group>
         </Space>
       </Modal>
       
       <Modal
         title="确认复制课程"
         open={copyCourseModal}
         onCancel={() => setCopyCourseModal(false)}
         footer={[
           <Button key="cancel" onClick={() => setCopyCourseModal(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" onClick={() => handleCopyCourse(copySlotId)}>
             确认复制
           </Button>,
         ]}
       >
         <p>是否确认复制该会员最近的课程到当前时间段？</p>
       </Modal>

       <Modal
         title="删除确认"
         open={deleteModalVisible}
         onCancel={() => setDeleteModalVisible(false)}
         footer={[
           <Button key="cancel" onClick={() => setDeleteModalVisible(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" danger onClick={handleDeleteConfirm}>
             确认删除
           </Button>,
         ]}
       >
         <p>确定要删除该时间段的排课吗？</p>
       </Modal>

       <Modal
         title="移除会员确认"
         open={removeMemberModalVisible}
         onCancel={() => setRemoveMemberModalVisible(false)}
         footer={[
           <Button key="cancel" onClick={() => setRemoveMemberModalVisible(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" onClick={handleRemoveMemberConfirm}>
             确认移除
           </Button>,
         ]}
       >
         <p>确定要移除该会员吗？</p>
       </Modal>

       <Modal
         title="保存确认"
         open={singleSaveModalVisible}
         onCancel={() => setSingleSaveModalVisible(false)}
         footer={[
           <Button key="cancel" onClick={() => setSingleSaveModalVisible(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" onClick={handleSingleSaveConfirm}>
             确认保存
           </Button>,
         ]}
       >
         <p>确定要保存该明细吗？</p>
       </Modal>

       {/* 训练反馈弹窗 */}
       {currentTrainingRecord && (
         <PostClassFeedback
           currentScheduleMember={currentTrainingRecord.members?.[0] || null}
           totalRecord={1}
           completeClass={currentTrainingRecord.completeClass || ''}
           setFinishConfirmVisible={() => {}}
           loading={false}
           scheduleModalVisible={trainingFeedbackModalVisible}
           setScheduleModalVisible={setTrainingFeedbackModalVisible}
           summaryViewMode={true}
           setSummaryViewMode={() => {}}
           suggestionViewMode={true}
           setSuggestionViewMode={() => {}}
           normalizeRichText={(raw) => raw}
           scheduleForm={trainingFeedbackForm}
           summaryRef={null}
           suggestionRef={null}
           handleCopyPostClassHistory={() => {}}
           sportsOptions={[]}
           visibleTabs={['1', '2']}
           handleDateChange={() => {}}
           scheduleRef={trainingFeedbackScheduleRef}
           prescriptionId={typeof currentTrainingRecord.id === 'number' ? currentTrainingRecord.id : null}
         />
       )}

       {/* 未来排课弹窗 */}
       <Modal
         title="未来排课"
         open={futureSchedulingModalVisible}
         onCancel={() => setFutureSchedulingModalVisible(false)}
         footer={[
           <Button key="cancel" onClick={() => setFutureSchedulingModalVisible(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" onClick={handleConfirmScheduling}>
             确认排课
           </Button>,
         ]}
       >
         <div>
           <p style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
             <span>请选择要排课的日期（已排课日期不可选）：</span>
             <Button 
               type="text" 
               onClick={() => setDatePickerOpen(false)}
               style={{ padding: '0', fontSize: '16px' }}
             >
               ×
             </Button>
           </p>
           <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px' }}>
             <DatePicker
               picker="date"
               value={null}
               open={datePickerOpen}
               onOpenChange={setDatePickerOpen}
               disabledDate={(date) => {
                 const today = dayjs().startOf('day');
                 const oneMonthLater = today.add(1, 'month');
                 // 确保格式一致进行比较
                 const formattedDate = date.format('YYYY-MM-DD');
                 // 如果日期已在已选列表中或在禁用列表中，则禁用
                 const isSelected = selectedDates.some(d => d.format('YYYY-MM-DD') === formattedDate);
                 return date.isBefore(today, 'day') || date.isAfter(oneMonthLater, 'day') || disabledDates.includes(formattedDate) || isSelected;
               }}
               onChange={(date) => {
                 if (date) {
                   const formattedDate = date.format('YYYY-MM-DD');
                   // 检查是否已选择该日期
                   const isSelected = selectedDates.some(d => d.format('YYYY-MM-DD') === formattedDate);
                   if (!isSelected) {
                     // 如果未选择，则添加到选择列表
                     setSelectedDates([...selectedDates, date]);
                   }
                 }
               }}
               style={{ width: '100%', marginBottom: '8px' }}
             />
             <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
               {selectedDates.map((date, index) => (
                 <Tag
                   key={index}
                   closable
                   onClose={(e) => {
                     e.preventDefault();
                     setSelectedDates(selectedDates.filter((_, i) => i !== index));
                   }}
                   color="blue"
                 >
                   {date.format('MM-DD')}
                 </Tag>
               ))}
             </div>
           </div>
           <p style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
             已选择 {selectedDates.length} 个日期
           </p>
         </div>
       </Modal>

       {/* 变更时间弹窗 */}
       <Modal
         title="变更时间"
         open={changeTimeModalVisible}
         onCancel={() => setChangeTimeModalVisible(false)}
         footer={[
           <Button key="cancel" onClick={() => setChangeTimeModalVisible(false)}>
             取消
           </Button>,
           <Button key="confirm" type="primary" onClick={handleChangeTimeConfirm}>
             确认变更
           </Button>,
         ]}
       >
         <Space direction="vertical" style={{ width: '100%' }}>
           <div>
             <Text>选择新日期：</Text>
             <DatePicker
               value={selectedNewDate}
               onChange={setSelectedNewDate}
               style={{ width: '100%' }}
               format="YYYY-MM-DD"
             />
           </div>
           <div>
             <Text>选择新时间：</Text>
             <Select
               placeholder="请选择时间"
               value={selectedNewTime ? selectedNewTime.format(formatTime) : undefined}
               onChange={(value) => {
                 if (value) {
                   const [hour, minute] = value.split(':').map(Number);
                   const newTime = selectedNewDate ? selectedNewDate.hour(hour).minute(minute) : dayjs().hour(hour).minute(minute);
                   setSelectedNewTime(newTime);
                 }
               }}
               style={{ width: '100%' }}
             >
               {timeSlots.map((slot) => (
                 <Select.Option key={slot.slotId} value={slot.slotId}>
                   {slot.label}
                 </Select.Option>
               ))}
             </Select>
           </div>
         </Space>
       </Modal>
     </div>
   );
 };

 export default ScheduCourse;

